import { InputProps } from "@material-tailwind/react/components/Input";
import { Add, Remove } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import React, { ReactElement } from "react";
import { Stateful } from "../../hooks/useStateful";
import { AlertIcon, ErrorIcon } from "../Icons/Icons";
import Text, { TextVariant } from "../Text/Text";

export type CounterProps = {
  id: string;
  label: string;
  showButtons?: boolean;
  error?: string;
  showErrorHelperText?: boolean;
  errorIcon?: boolean; //to use triangle
  onCountChanged?: (count: number) => void;
  minCount?: number;
  maxCount?: number;
  value?: number;
  state?: Stateful<number>;
  disabled?: boolean;
  helpText?: string;
} & Omit<InputProps, "ref" | "crossOrigin" | "error">;

const Counter: React.ForwardRefRenderFunction<HTMLDivElement, CounterProps> = (
  {
    id,
    label,
    showButtons = true,
    onCountChanged,
    minCount = 0,
    maxCount = 1000,
    value,
    error,
    disabled,
    showErrorHelperText = true,
    errorIcon,
    ...rest
  },
  ref,
) => {
  const handleCountChanged = (count: number) => {
    onCountChanged && onCountChanged(count);
  };

  const input: ReactElement = (
    <div
      className={`flex justify-center items-center h-12 min-w-12 max-w-12 
        ${
          showButtons
            ? error
              ? "bg-ldir-red-700 border border-ldir-red-900"
              : " border border-ldir-blue-pale"
            : ""
        } `}
      id={id + "CounterInput"}
      data-testid={id + "CounterInput"}
    >
      <Text variant={TextVariant.xxxlarge}>{value}</Text>
    </div>
  );

  let iconButtonClassnames = "h-12 w-12 bg-ldir-blue-800 disabled:text-gray-400";
  const minusDisabled = value === undefined || value <= 0 || disabled;
  const plusDisabled = value === undefined || value >= maxCount || disabled;

  return (
    <div>
      <div
        className="flex flex-row justify-between items-center border-t border-ldir-blue-pale py-4"
        ref={ref}
      >
        <div className="col-span-4">
          <Text variant={TextVariant.large}>{label}</Text>

          {error && (
            <div className="text-ldir-red-900 flex gap-1 pt-1">
              {errorIcon ? (
                <ErrorIcon sizeClassName="h-5 w-6" title="feil" />
              ) : (
                <AlertIcon title="feil" />
              )}
              {showErrorHelperText && <Text variant={TextVariant.small}>{error}</Text>}
            </div>
          )}
        </div>
        <div className="flex flex-row items-center">
          {showButtons ? (
            <IconButton
              title="minus"
              disabled={minusDisabled}
              onClick={() => handleCountChanged(Math.max((value || 0) - 1, minCount))}
              id={id + "CounterMinusButton"}
              data-testid={id + "CounterMinusButton"}
              className={`${iconButtonClassnames} rounded-l rounded-r-none mr-0.5  ${
                minusDisabled ? " bg-ldir-blue-800/40" : ""
              }`}
            >
              <Remove className="h-11 w-11 text-ldir-blue-100" />
            </IconButton>
          ) : (
            <div className="w-12 h-12 mr-0.5" />
          )}
          {input}
          {showButtons ? (
            <IconButton
              title="pluss"
              disabled={plusDisabled}
              onClick={() => handleCountChanged(Math.min((value || 0) + 1, maxCount))}
              id={id + "CounterPlusButton"}
              data-testid={id + "CounterPlusButton"}
              className={`${iconButtonClassnames} rounded-r rounded-l-none ml-0.5 ${
                plusDisabled ? " bg-ldir-blue-800/40" : ""
              }`}
            >
              <Add className="h-11 w-11  text-ldir-blue-100" />
            </IconButton>
          ) : (
            <div className="w-12 h-12 ml-0.5" />
          )}
        </div>
      </div>
      {rest.helpText && <Text variant={TextVariant.small}>{rest.helpText}</Text>}
    </div>
  );
};

export default React.forwardRef(Counter);
